import React, { useState, useCallback, useRef, useEffect } from 'react';
import './CustomPDFViewer.css';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = ({ blob, fileName = 'documento.pdf' }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const wrapperRef = useRef(null);
  const touchStartDistanceRef = useRef(0);
  const touchStartZoomRef = useRef(1.0);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNum(1);
    setIsLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('Erro ao carregar PDF:', error);
    setIsLoading(false);
  };

  const prevPage = () => setPageNum(p => Math.max(1, p - 1));
  const nextPage = () => setPageNum(p => Math.min(numPages || 1, p + 1));
  
  const zoomIn = () => setZoomLevel(s => Math.min(3, s + 0.25));
  const zoomOut = () => setZoomLevel(s => Math.max(0.75, s - 0.25));
  const resetZoom = () => setZoomLevel(1.0);

  // Pinch-zoom com touch
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      touchStartDistanceRef.current = Math.sqrt(dx * dx + dy * dy);
      touchStartZoomRef.current = zoomLevel;
    }
  }, [zoomLevel]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      const currentDistance = Math.sqrt(dx * dx + dy * dy);
      
      const ratio = currentDistance / touchStartDistanceRef.current;
      const newZoom = touchStartZoomRef.current * ratio;
      setZoomLevel(Math.max(0.75, Math.min(3, newZoom)));
    }
  }, []);

  // Setup event listeners
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    wrapper.addEventListener('touchstart', handleTouchStart, false);
    wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleTouchStart, handleTouchMove]);

  // Função para fazer download do PDF
  const handleDownload = useCallback(() => {
    if (!blob) return;
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [blob, fileName]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!blob) {
    return (
      <div className="pdfviewer-empty">
        <p>Sem material para exibir</p>
      </div>
    );
  }

  // Se estiver em fullscreen no mobile, renderiza apenas o PDF
  if (isFullscreen) {
    return (
      <div className="pdfviewer-fullscreen">
        <div className="pdfviewer-fullscreen-header">
          <button onClick={toggleFullscreen} className="pdfviewer-close-fullscreen" aria-label="Fechar tela cheia">
            ✕
          </button>
        </div>
        <div className="pdfviewer-fullscreen-content">
          <Document
            file={blob}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            className="pdfviewer-document"
            loading={null}
          >
            <Page
              pageNumber={pageNum}
              scale={1.5}
              renderMode="canvas"
              className="pdfviewer-page-render"
            />
          </Document>
        </div>
        <div className="pdfviewer-fullscreen-footer">
          <button onClick={prevPage} disabled={pageNum <= 1}>‹</button>
          <span>{pageNum} / {numPages || '...'}</span>
          <button onClick={nextPage} disabled={pageNum >= (numPages || 1)}>›</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdfviewer-root">
      <div className="pdfviewer-toolbar">
        <div className="pdfviewer-nav">
          <button onClick={prevPage} disabled={pageNum <= 1} aria-label="Página anterior">
            ‹
          </button>
          <span className="pdfviewer-page">
            {pageNum} / {numPages || '...'}
          </span>
          <button onClick={nextPage} disabled={pageNum >= (numPages || 1)} aria-label="Próxima página">
            ›
          </button>
        </div>

        <div className="pdfviewer-zoom">
          <button onClick={zoomOut} aria-label="Diminuir zoom">
            −
          </button>
          <span>{(zoomLevel * 100).toFixed(0)}%</span>
          <button onClick={zoomIn} aria-label="Aumentar zoom">
            +
          </button>
        </div>

        <button 
          onClick={handleDownload} 
          className="pdfviewer-download-btn" 
          aria-label="Baixar PDF" 
          title="Baixar PDF"
        >
          ⬇
        </button>

        <button 
          onClick={toggleFullscreen} 
          className="pdfviewer-fullscreen-btn" 
          aria-label="Tela cheia" 
          title="Tela cheia"
        >
          ⛶
        </button>
      </div>

      <div className="pdfviewer-canvas-wrap" ref={wrapperRef}>
        {isLoading && (
          <div className="pdfviewer-loading">
            <div className="pdfviewer-spinner"></div>
            <p>Carregando PDF...</p>
          </div>
        )}
        
        <div className="pdfviewer-document-container" style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top left' }}>
          <Document
            file={blob}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            className="pdfviewer-document"
            loading={null}
          >
            <Page
              pageNumber={pageNum}
              scale={1.0}
              renderMode="canvas"
              className="pdfviewer-page-render"
            />
          </Document>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
